import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { ConfirmDialogService } from './confirm-dialog.service';

@Component({
    selector: 'app-confirm-dialog',
    standalone: true,
    imports: [CommonModule],
    animations: [
        trigger('backdropAnim', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('200ms ease-out', style({ opacity: 1 })),
            ]),
            transition(':leave', [
                animate('150ms ease-in', style({ opacity: 0 })),
            ]),
        ]),
        trigger('dialogAnim', [
            transition(':enter', [
                style({ opacity: 0, transform: 'scale(0.9) translateY(10px)' }),
                animate('250ms ease-out', style({ opacity: 1, transform: 'scale(1) translateY(0)' })),
            ]),
            transition(':leave', [
                animate('150ms ease-in', style({ opacity: 0, transform: 'scale(0.95) translateY(5px)' })),
            ]),
        ]),
    ],
    template: `
        @if (dialogService.isOpen()) {
            <!-- Backdrop -->
            <div @backdropAnim
                 class="fixed inset-0 z-[99998] bg-black/50 backdrop-blur-sm"
                 (click)="dialogService.onCancel()">
            </div>

            <!-- Dialog -->
            <div @dialogAnim
                 class="fixed inset-0 z-[99999] flex items-center justify-center p-4">
                <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full border border-gray-200 dark:border-gray-700 overflow-hidden"
                     (click)="$event.stopPropagation()">
                    <!-- Header -->
                    <div class="px-6 pt-6 pb-2 flex items-start gap-4">
                        <!-- Icon -->
                        <div class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                             [ngClass]="{
                                 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400': dialogService.config().type === 'danger',
                                 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400': dialogService.config().type === 'warning',
                                 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400': dialogService.config().type === 'info'
                             }">
                            @if (dialogService.config().type === 'danger') {
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                </svg>
                            }
                            @if (dialogService.config().type === 'warning') {
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                                </svg>
                            }
                            @if (dialogService.config().type === 'info') {
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                            }
                        </div>
                        <div class="flex-1">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                                {{ dialogService.config().title }}
                            </h3>
                            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                {{ dialogService.config().message }}
                            </p>
                            @if (dialogService.config().details) {
                                <p class="mt-2 text-xs text-gray-500 dark:text-gray-500 whitespace-pre-line bg-gray-50 dark:bg-gray-900/50 rounded-md p-2">
                                    {{ dialogService.config().details }}
                                </p>
                            }
                        </div>
                    </div>

                    <!-- Actions -->
                    <div class="px-6 py-4 flex justify-end gap-3">
                        <button (click)="dialogService.onCancel()"
                                class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-500">
                            {{ dialogService.config().cancelText }}
                        </button>
                        <button (click)="dialogService.onConfirm()"
                                class="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                                [ngClass]="{
                                    'bg-red-600 hover:bg-red-700 focus:ring-red-500': dialogService.config().type === 'danger',
                                    'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500': dialogService.config().type === 'warning',
                                    'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500': dialogService.config().type === 'info'
                                }">
                            {{ dialogService.config().confirmText }}
                        </button>
                    </div>
                </div>
            </div>
        }
    `,
})
export class ConfirmDialogComponent {
    readonly dialogService = inject(ConfirmDialogService);
}
